import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableFilterMetaData } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { ShippingRateLima } from "../../Domain/Model/ShippingRateLima";
import { ShippingRateProvince } from "../../Domain/Model/ShippingRateProvince";
import { AccessLevel, LimaZone, Region, SizeCategory } from "../enum/ShippingEnums";
import "./ShippingView.css";
import ShippingViewModel from "./ShippingViewModel";

const sizeCategoryName = (value: any): string => {
    switch (value) {
        case SizeCategory.XXS:
        case SizeCategory.XS:
        case SizeCategory.S:
        case SizeCategory.M:
        case SizeCategory.L:
            return String(value);
        default:
            return String(value ?? "");
    }
};

export const ShippingView = () => {
    const {
        getShippingRatesProvince,
        getShippingRatesLima,
        shippingRatesProvince,
        shippingRatesLima,
    } = ShippingViewModel();

    const [filtersProvince, setFiltersProvince] = useState<DataTableFilterMeta>({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [filtersLima, setFiltersLima] = useState<DataTableFilterMeta>({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });

    useEffect(() => {
        getShippingRatesProvince();
        getShippingRatesLima();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const regionName = (value: any): string => {
        switch (value) {
            case Region.COSTA:
                return "Costa";
            case Region.SIERRA:
                return "Sierra";
            case Region.SELVA:
                return "Selva";
            default:
                return String(value ?? "");
        }
    };

    const accessLevelName = (value: any): string => {
        switch (value) {
            case AccessLevel.FACIL:
                return "Fácil";
            case AccessLevel.MEDIANO:
                return "Mediano";
            case AccessLevel.DIFICIL:
                return "Difícil";
            case AccessLevel.MUY_DIFICIL:
                return "Muy difícil";
            default:
                return String(value ?? "");
        }
    };

    const limaZoneName = (value: any): string => {
        switch (value) {
            case LimaZone.NORTE:
                return "Lima Norte";
            case LimaZone.SUR:
                return "Lima Sur";
            case LimaZone.ESTE:
                return "Lima Este";
            case LimaZone.OESTE:
                return "Lima Oeste";
            case LimaZone.CENTRO:
                return "Lima Centro";
            case LimaZone.CALLAO:
                return "Callao";
            default:
                return String(value ?? "");
        }
    };

    const formatPrice = (price: any): string => {
        if (price === null || price === undefined) return "-";
        const number = Number(price);
        return Number.isNaN(number) ? String(price) : `S/ ${number.toFixed(2)}`;
    };

    const estimatedBadge = (data: ShippingRateLima) => {
        return (
            <span className={data.isEstimated ? "badge rounded-pill bg-warning text-dark" : "badge rounded-pill bg-success"}>
                {data.isEstimated ? "Estimado" : "Exacto"}
            </span>
        );
    };

    const provinceTableHeader = () => {
        return (
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <h5 className="m-0">Tarifas de envío por provincia</h5>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" value={(filtersProvince['global'] as DataTableFilterMetaData)?.value || ''} onChange={(e) => setFiltersProvince({ global: { value: e.target.value || null, matchMode: FilterMatchMode.CONTAINS } })} placeholder="Buscar..." />
                </span>
            </div>
        );
    };

    const limaTableHeader = () => {
        return (
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <h5 className="m-0">Tarifas de envío para Lima</h5>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" value={(filtersLima['global'] as DataTableFilterMetaData)?.value || ''} onChange={(e) => setFiltersLima({ global: { value: e.target.value || null, matchMode: FilterMatchMode.CONTAINS } })} placeholder="Buscar..." />
                </span>
            </div>
        );
    };

    return (
        <>
            <div className="container-fluid">
                <div className="block-header">
                    <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-12 text-start">
                            <h2>Envíos</h2>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="/home" className="text-danger"><FontAwesomeIcon icon={icon({name: 'home'})} /></a>
                                </li>
                                <li className="breadcrumb-item">Tarifas de envío</li>
                            </ul>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <div className="d-flex flex-row-reverse">
                                <div className="p-2 d-flex"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className='title-report'>
                        Aquí puedes ver las tarifas de envío configuradas
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-6 col-md-12">
                        <div className="card">
                            <DataTable
                                value={shippingRatesProvince}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                tableStyle={{ minWidth: '42rem' }}
                                emptyMessage="No se encontraron tarifas."
                                filters={filtersProvince}
                                globalFilterFields={['region', 'accessLevel', 'size', 'price']}
                                header={provinceTableHeader}
                            >
                                <Column field="index" header="N°" headerClassName="text-center" bodyClassName="text-center" body={(rowData, options) => options.rowIndex + 1} />
                                <Column field="region" header="Región" body={(rowData: ShippingRateProvince) => regionName(rowData.region)} />
                                <Column field="accessLevel" header="Nivel de acceso" body={(rowData: ShippingRateProvince) => accessLevelName(rowData.accessLevel)} />
                                <Column field="size" header="Categoría de tamaño" body={(rowData: ShippingRateProvince) => sizeCategoryName(rowData.size)} />
                                <Column field="price" header="Precio" body={(rowData: ShippingRateProvince) => formatPrice(rowData.price)} />
                            </DataTable>
                        </div>
                    </div>
                    <div className="col-lg-6 col-md-12">
                        <div className="card">
                            <DataTable
                                value={shippingRatesLima}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                tableStyle={{ minWidth: '42rem' }}
                                emptyMessage="No se encontraron tarifas."
                                filters={filtersLima}
                                globalFilterFields={['zone', 'size', 'price', 'isEstimated']}
                                header={limaTableHeader}
                            >
                                <Column field="index" header="N°" headerClassName="text-center" bodyClassName="text-center" body={(rowData, options) => options.rowIndex + 1} />
                                <Column field="zone" header="Zona" body={(rowData: ShippingRateLima) => limaZoneName(rowData.zone)} />
                                <Column field="size" header="Categoría de tamaño" body={(rowData: ShippingRateLima) => sizeCategoryName(rowData.size)} />
                                <Column field="price" header="Precio" body={(rowData: ShippingRateLima) => formatPrice(rowData.price)} />
                                <Column field="isEstimated" header="¿Es estimado?" body={estimatedBadge} />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};